import type { ModeId, PropKind, Side } from './defs.ts';
import type { Rect } from './sim/movement.ts';
import type { MapDoor, MapPoly, MapRoof } from './geom.ts';
import { CAUSEWAY } from './maps/causeway.ts';
import { OLDTOWN } from './maps/oldtown.ts';
import { PLAZA } from './maps/plaza.ts';
import { RANGE_MAP } from './maps/range.ts';
import type { RangeLayout } from './range.ts';
import { QUARRY } from './maps/quarry.ts';
import { MARKET } from './maps/market.ts';
import { MUSEUM } from './maps/museum.ts';
import { SUBPEN } from './maps/subpen.ts';
import { PARK } from './maps/park.ts';
import { WASTELAND } from './maps/wasteland.ts';
import { RAILYARD } from './maps/railyard.ts';
import { SUMMIT } from './maps/summit.ts';
import { GEO_TEST } from './maps/geotest.ts';
import { EMBASSY } from './maps/embassy.ts';
import { AIRBASE } from './maps/airbase.ts';
import { OUTPOST } from './maps/outpost.ts';

export type Center = { x: number; y: number };
export type WallMaterial = 'concrete' | 'sandstone' | 'planter' | 'stall' | 'shopfront' | 'stack' | 'cart' | 'shrine' | 'gallery' | 'marble' | 'vitrine' | 'plinth' | 'counter' | 'hull' | 'tower' | 'bulkhead' | 'rack' | 'water' | 'hedge' | 'pond' | 'parkstone' | 'trunk' | 'bench' | 'play' | 'hangar' | 'cinder' | 'sbags' | 'gse' | 'bunk' | 'pallet' | 'jersey' | 'terminus' | 'ironwork' | 'boxcar' | 'sleepers' | 'kiosk' | 'coalheap' | 'timber' | 'hearth' | 'bartop' | 'drift' | 'machine' | 'embwall' | 'embglass' | 'embfurn' | 'embrack' | 'embhedge' | 'rubble' | 'scrap' | 'qledge' | 'qblock' | 'qplant' | 'qcabin' | 'qshed' | 'otwall' | 'otstone' | 'otfurn' | 'otstall' | 'rgwall' | 'rgbags' | 'rgblast' | 'rgbench' | 'rgtable' | 'rgtower' | 'rghut' | 'rgply' | 'rgberm' | 'ophesco' | 'opbags' | 'optower' | 'optent' | 'opcont' | 'optruck' | 'opcrate' | 'opfuel' | 'opwreck';
/** A map's own look (src/client/themes): its floor, wall art, decor and lights. */
export type ThemeId = 'market' | 'museum' | 'subpen' | 'park' | 'summit' | 'railyard' | 'harbor' | 'embassy' | 'airbase' | 'wasteland' | 'quarry' | 'oldtown' | 'plaza' | 'range' | 'outpost';
export type MapWall = Rect & { material: WallMaterial };

export const ZONE_RADIUS = 180;
export const CRATE_SIZE = 44;

export type MapDef = {
  name: string;
  /** The visual theme this map wears; omitted for the plain yard look. */
  theme?: ThemeId;
  size: number;
  walls: readonly MapWall[];
  /** Non-rectangular solids (planes, hulls, round tanks); see docs/maps/GEOMETRY.md. */
  polys?: readonly MapPoly[];
  /** Sliding and swinging doors, server-driven. */
  doors?: readonly MapDoor[];
  /** Roofs drawn over interiors, fading out while you stand inside. */
  roofs?: readonly MapRoof[];
  /** DOM capture points A, B and C. */
  zones: readonly Center[];
  /** Every point inside a region is a clear spot for a player's center. */
  spawns: { red: readonly Rect[]; blue: readonly Rect[]; ffa: readonly Rect[] };
  crates: readonly Center[];
  /** Explosive barrels (versus modes), centers; half-turn twins like the crates. */
  barrels: readonly Center[];
  /** The other shootable props (versus modes), centers; half-turn twins of the same kind. */
  props: readonly (Center & { kind: PropKind })[];
  /** Zombies only: the core the squad defends and the edge strips the horde walks in from. */
  siege?: { core: Center; horde: Readonly<Record<Side, Rect>> };
  /** The shooting range only (mode RNG): where its targets stand and what the floor paints. */
  range?: RangeLayout;
};

export const MAP_IDS = ['causeway', 'plaza', 'oldtown', 'quarry', 'market', 'museum', 'subpen', 'park', 'railyard', 'summit', 'outpost', 'range', 'embassy', 'airbase', 'wasteland'] as const;
/** `geo-test` is the geometry test range: a real map for the sim, but in no rotation (see src/shared/maps/geotest.ts). */
export type MapId = (typeof MAP_IDS)[number] | 'geo-test';

export const MAPS: Record<MapId, MapDef> = {
  causeway: CAUSEWAY,
  plaza: PLAZA,
  oldtown: OLDTOWN,
  quarry: QUARRY,
  market: MARKET,
  museum: MUSEUM,
  subpen: SUBPEN,
  park: PARK,
  wasteland: WASTELAND,
  railyard: RAILYARD,
  summit: SUMMIT,
  embassy: EMBASSY,
  airbase: AIRBASE,
  'geo-test': GEO_TEST,
  range: RANGE_MAP,
  outpost: OUTPOST,
};

/** Where players on a map keep heading, whoever they are: its zones, each team's spawn and the middle of the map. */
export function landmarks(map: MapId): Center[] {
  const def = MAPS[map];
  const centre = (rs: readonly Rect[]): Center[] => {
    if (rs.length === 0) return [];
    const x0 = Math.min(...rs.map((r) => r.x)), y0 = Math.min(...rs.map((r) => r.y));
    const x1 = Math.max(...rs.map((r) => r.x + r.w)), y1 = Math.max(...rs.map((r) => r.y + r.h));
    return [{ x: (x0 + x1) / 2, y: (y0 + y1) / 2 }];
  };
  return [...def.zones, ...centre(def.spawns.red), ...centre(def.spawns.blue), { x: def.size / 2, y: def.size / 2 }];
}

export const ROTATION: Record<ModeId, readonly MapId[]> = {
  FFA: ['plaza', 'oldtown', 'museum', 'subpen', 'causeway', 'market', 'quarry', 'park', 'railyard', 'summit', 'embassy', 'airbase', 'wasteland'],
  TDM: ['causeway', 'plaza', 'market', 'museum', 'subpen', 'quarry', 'oldtown', 'park', 'railyard', 'summit', 'embassy', 'airbase', 'wasteland'],
  DOM: ['quarry', 'causeway', 'market', 'oldtown', 'museum', 'subpen', 'plaza', 'park', 'railyard', 'summit', 'embassy', 'airbase', 'wasteland'],
  ZOM: ['outpost'],
  BR: ['oldtown', 'quarry', 'plaza', 'causeway', 'park', 'subpen', 'museum', 'market', 'railyard', 'summit', 'embassy', 'airbase', 'wasteland'],
  RNG: ['range'],
};

/** How long a map lasts; every mode changes map when a round restarts. A round that nobody wins outright ends when this runs out. */
export const MAP_MS: Record<ModeId, number> = { FFA: 10 * 60_000, TDM: 12 * 60_000, DOM: 15 * 60_000, ZOM: Infinity, BR: Infinity, RNG: Infinity };
export const MAP_NOTICE_MS = 15_000;

const mix = (s: number): number => {
  let t = s | 0;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

/** One cycle of the mode's rotation in a seeded shuffle: every map once. A cycle never opens on the map the previous one closed with. */
function rotationBag(mode: ModeId, seed: number, cycle: number): MapId[] {
  const shuffled = (c: number): MapId[] => {
    const bag = [...ROTATION[mode]];
    let s = (seed ^ Math.imul(c + 1, 0x9e3779b1)) | 0;
    for (let k = bag.length - 1; k > 0; k--) {
      s = (s + 0x6d2b79f5) | 0;
      const j = Math.floor(mix(s) * (k + 1));
      [bag[k], bag[j]] = [bag[j]!, bag[k]!];
    }
    return bag;
  };
  const bag = shuffled(cycle);
  if (cycle > 0 && bag.length > 2 && bag[0] === shuffled(cycle - 1)[bag.length - 1]) [bag[0], bag[1]] = [bag[1]!, bag[0]!];
  return bag;
}

/**
 * The `i`th map a room plays: a random rotation, dealt like a shuffled deck so every map comes up once before any repeats and none plays twice
 * running. It is a pure function of the room's seed (no draw from the world's rng), so a replay deals the same maps.
 */
export function rotationMap(mode: ModeId, seed: number, i: number): MapId {
  const n = ROTATION[mode].length;
  return n < 2 ? ROTATION[mode][0]! : rotationBag(mode, seed, Math.floor(i / n))[i % n]!;
}

/** The map after the one a world is on, and its place in the deal (skipping a repeat of the current map, which a dev-chosen start can cause). */
export function nextMap(w: { mode: ModeId; map: MapId; rotationSeed: number; rotationAt: number }): { map: MapId; at: number } {
  let at = w.rotationAt + 1;
  if (ROTATION[w.mode].length > 1 && rotationMap(w.mode, w.rotationSeed, at) === w.map) at++;
  return { map: rotationMap(w.mode, w.rotationSeed, at), at };
}
